// The five personas as the onboarding wizard presents them: copy and steps.
//
// Which dashboards and workflows a persona sees is decided on the server
// (`firebase/functions/src/shared/navigation/rules.ts`), which every
// navigation surface reads through `useNavigation`.

import type { PersonaId, StepId } from "./personaConfigTypes";

// The core dashboards, gated per-user by product_scopes.
export type ModuleDashboardId =
  | "market_overview"
  | "market_deepdive"
  | "execution_view"
  | "physical_location"
  | "physical_franchise";

// Workspace-specific pages gated through the workflow-visibility registry or a
// per-user allowlist (see `gatedDashboards.ts`).
export type GatedDashboardId =
  | "accounts"
  | "key_accounts"
  | "global_key_accounts";

// Route-level dashboard identifiers, across both gating axes.
export type DashboardId = ModuleDashboardId | GatedDashboardId;

export interface PersonaDefinition {
  id: PersonaId;
  label: string;
  // Wizard persona-card copy.
  description: string;
  // The wizard flow after the persona step. Per-market steps repeat for each of
  // the user's assigned markets. Empty = the wizard completes right after
  // persona selection (power_user).
  steps: StepId[];
}

export const PERSONA_DEFINITIONS: Record<PersonaId, PersonaDefinition> = {
  kam: {
    id: "kam",
    label: "Key Account Manager",
    description:
      "Manage a set of key chains: audit their execution and track performance.",
    steps: ["premise", "chains"],
  },
  local_sales_manager: {
    id: "local_sales_manager",
    label: "Local Sales Manager",
    description:
      "Own a region: find new business and manage existing accounts.",
    steps: ["premise", "regions", "focus"],
  },
  ecommerce_manager: {
    id: "ecommerce_manager",
    label: "E-commerce Manager",
    description:
      "Own delivery platforms: track presence, pricing and share across them.",
    steps: ["premise", "platforms"],
  },
  brand_manager: {
    id: "brand_manager",
    label: "Brand Manager",
    description:
      "Own brands: track their performance, imagery and shelf presence.",
    steps: ["premise", "brands"],
  },
  power_user: {
    id: "power_user",
    label: "Power User",
    description: "Everything the workspace has, unfiltered.",
    steps: [],
  },
};

export const ALL_PERSONA_IDS = Object.keys(PERSONA_DEFINITIONS) as PersonaId[];

// How the onboarding premise answer reads wherever the role is summarised.
export const PREMISE_LABELS: Record<string, string> = {
  on_premise: "On-premise",
  off_premise: "Off-premise",
  both: "Both",
};
